import {
  BillingResult,
  ResultsStateData,
  PostcodeStateData,
  ClinicStateData,
  BillingGuideState,
  ResultsStatus,
} from '../domain/models/pages/BillingGuideLookupPage/BillingGuideLookupModel'
import { createSelector } from 'reselect'

// Base selectors (direct state access)
const selectPricing = (state: BillingGuideState) => state.pricing
const selectPostcodes = (state: BillingGuideState) => state.postcodes
const selectPostcodeInput = (state: BillingGuideState) => state.postcodeInput
const selectClinics = (state: BillingGuideState) => state.clinics
const selectSelectedClinic = (state: BillingGuideState) => state.selectedClinic
const selectError = (state: BillingGuideState) => state.error
const selectLoading = (state: BillingGuideState) => state.loading

// Memoized selectors
export const selectTransformedPricingResults = createSelector([selectPricing], (pricing): BillingResult[] => {
  if (!pricing) return []
  return pricing.map((item) => ({
    location: item.clinicName,
    // If subModality is the same as modality, just show modality (e.g. "General x-ray"), otherwise show "Modality - SubModality" (e.g. "Ultrasound - General")
    procedure:
      item.modalityName === item.subModalityName ? item.modalityName : `${item.modalityName} - ${item.subModalityName}`,
    pensionerGap: item.pensionerGap,
    privateGap: item.privateGap,
    sourceTimestamp: item.metadata?.processedAt,
  }))
})

export const selectSearchStatus = createSelector(
  [selectLoading, selectError, selectPricing],
  (loading, error, pricing): ResultsStatus => {
    if (loading.pricing) return ResultsStatus.SEARCHING
    if (error.pricing) return ResultsStatus.ERROR

    // search not yet attempted
    if (pricing === null) return ResultsStatus.IDLE

    // search was performed
    if (pricing.length > 0) return ResultsStatus.SUCCESS

    // search was performed but no results
    return ResultsStatus.NO_RESULTS
  },
)

export const selectPostcodeProps = createSelector(
  [selectPostcodes, selectPostcodeInput, selectError, selectLoading],
  (postcodes, postcodeInput, error, loading): PostcodeStateData => ({
    suggestions: postcodes || [],
    value: postcodeInput,
    error: error.postcodes,
    loading: loading.postcodes,
  }),
)

export const selectClinicProps = createSelector(
  [selectClinics, selectSelectedClinic, selectError, selectLoading],
  (clinics, selectedClinic, error, loading): ClinicStateData => ({
    items: clinics,
    selectedItem: selectedClinic,
    error: error.clinics,
    loading: loading.clinics,
  }),
)

export const selectResultsProps = createSelector(
  [selectTransformedPricingResults, selectSearchStatus, selectPricing, selectError],
  (results, status, pricing, error): ResultsStateData => ({
    results,
    status,
    sourceTimestamp: pricing?.[0]?.metadata?.processedAt,
    error: error.pricing,
  }),
)
